import { Injectable } from '@angular/core';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { timeout, catchError, of, Observable, map } from 'rxjs';
import { ToastService } from '../toast/toast.service';

@Injectable({
  providedIn: 'root'
})
export class GoogleRecaptchaService {

  constructor(
    private _toast: ToastService,
    private recaptchaV3Service: ReCaptchaV3Service) { }

  initializeToken(): Observable<string | null> {
    // If the reCAPTCHA JS failed to load, surface a clear error immediately
    if (!this.isRecaptchaAvailable()) {
      this._toast.showPopup(
        'WARNING',
        'reCAPTCHA Unavailable',
        'reCAPTCHA failed to load. Please disable ad blockers, check your network/CSP, and reload the page.',
        10000
      );
      return of(null);
    }

    return this.recaptchaV3Service.execute('importantAction').pipe(
      timeout(10000),
      map((token: any) => {
        return (typeof token === 'string' && token.length > 0) ? token : null;
      }),
      catchError((error: any) => {
        this.handleRecaptchaError(error);
        return of(null);
      })
    );
  }

  private handleRecaptchaError(error: any): void {
    let errorMessage = 'Unknown reCAPTCHA error occurred.';
    let errorTitle = 'Error';
    let errorType = 'ERROR';

    if (error.name === 'TimeoutError') {
      errorMessage = 'reCAPTCHA verification timed out. Please check your internet connection and try again.';
      errorTitle = 'Timeout Error';
    } else if (error.name === 'NetworkError' || error.message?.includes('network')) {
      errorMessage = 'Network error occurred during reCAPTCHA verification. Please check your internet connection.';
      errorTitle = 'Network Error';
    } else if (error.message?.includes('sitekey')) {
      errorMessage = 'reCAPTCHA configuration error. Please contact support.';
      errorTitle = 'Configuration Error';
      errorType = 'WARNING';
    } else if (error.message?.includes('blocked') || error.message?.includes('blocked')) {
      errorMessage = 'reCAPTCHA is blocked by your browser or ad blocker. Please disable ad blockers and try again.';
      errorTitle = 'Access Blocked';
      errorType = 'WARNING';
    } else if (error.message?.includes('invalid')) {
      errorMessage = 'Invalid reCAPTCHA response. Please refresh the page and try again.';
      errorTitle = 'Invalid Response';
    } else if (error.message?.includes('expired')) {
      errorMessage = 'reCAPTCHA token expired. Please refresh the page and try again.';
      errorTitle = 'Token Expired';
    } else if (error.status === 429) {
      errorMessage = 'Too many reCAPTCHA requests. Please wait a moment before trying again.';
      errorTitle = 'Rate Limited';
      errorType = 'WARNING';
    } else if (error.status >= 500) {
      errorMessage = 'reCAPTCHA service is temporarily unavailable. Please try again later.';
      errorTitle = 'Service Unavailable';
      errorType = 'WARNING';
    }

    // Log detailed error for debugging
    console.error('reCAPTCHA Error Details:', {
      name: error.name,
      message: error.message,
      status: error.status,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Show user-friendly error message
    this._toast.showPopup(errorType, errorTitle, errorMessage, 10000);
  }

  // Method to check if reCAPTCHA is available and properly configured
  isRecaptchaAvailable(): boolean {
    try {
      const w: any = typeof window !== 'undefined' ? (window as any) : null;
      const grecaptcha = w && w.grecaptcha ? w.grecaptcha : null;
      return !!(
        grecaptcha &&
        typeof grecaptcha.ready === 'function' &&
        typeof grecaptcha.execute === 'function'
      );
    } catch (error) {
      console.warn('reCAPTCHA availability check failed:', error);
      return false;
    }
  }

  // Method to handle reCAPTCHA initialization errors
  handleInitializationError(): void {
    if (!this.isRecaptchaAvailable()) {
      this._toast.showPopup('WARNING', 'reCAPTCHA Unavailable', 
        'reCAPTCHA is not available. Some features may be limited.', 5000);
    }
  }
}
